import React, { useState, useEffect, useRef } from 'react';
import { Link } from "react-router-dom";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import { PlusCircle, ChevronUp } from "feather-icons-react/build/IconComponents";
import { useDispatch, useSelector } from "react-redux";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { setToogleHeader } from "../../core/redux/action";
import * as bootstrap from "bootstrap";
import { useToast } from '../../Toaster/ToastContext';
import Loader from '../loader/loader';
import withReactContent from 'sweetalert2-react-content';
import Table from '../../core/pagination/datatable';
import { apiClient } from '../../services/apiClient';

const ExpensesList = () => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const [selectedDateModal, setSelectedDateModal] = useState(null);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const { showToast } = useToast();
  const hasFetched = useRef(false);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editExpenseId, setEditExpenseId] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const [formData, setFormData] = useState({
    expense_category_id: '',
    branch_id: '',
    description: '',
    date: '',
    amount: ''
  });

  const [errors, setErrors] = useState({
    expense_category_id: '',
    branch_id: '',
    date: '',
    amount: '',
  });

  const validateField = (name, value) => {
    switch (name) {
      case 'expense_category_id':
        return value ? '' : 'Select expense category';
      case 'branch_id':
        return value ? '' : 'Select branch';
      case 'date':
        return value ? '' : 'Date is required';
      case 'amount':
        return value ? '' : 'Amount is required';
      default:
        return '';
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleCategoryChange = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : '';
    setFormData({ ...formData, expense_category_id: value });
    setErrors((prev) => ({ ...prev, expense_category_id: validateField('expense_category_id', value) }));
  };

  const handleBranchChange = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : '';
    setFormData({ ...formData, branch_id: value });
    setErrors((prev) => ({ ...prev, branch_id: validateField('branch_id', value) }));
  };

  useEffect(() => {
    const fetchExpenseCategories = async () => {
      try {
        const response = await apiClient.get('/get-all-expenses-category');
        if (response.success) {
          setExpenseCategories(response.data);
        }
      } catch (error) {
        showToast('Failed to fetch expenses:', error, 'error');
      }
    };
    fetchExpenseCategories();
  }, []);

  useEffect(() => {
    const fetchExpenseBranches = async () => {
      try {
        const response = await apiClient.get('/get-all-branch');
        if (response.success) {
          setBranches(response.data);
        }
      } catch (error) {
        showToast('Failed to fetch branches:', error, 'error');
      }
    };
    fetchExpenseBranches();
  }, []);

  const validateForm = () => {
    const newErrors = {
      expense_category_id: validateField('expense_category_id', formData.expense_category_id),
      branch_id: validateField('branch_id', formData.branch_id),
      date: validateField('date', formData.date),
      amount: validateField('amount', formData.amount),
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== '');
  };

  const handleAdd = () => {
    const modalElement = document.getElementById('add-units');
    const modal = new bootstrap.Modal(modalElement);
    modal.show();
    setFormData({ expense_category_id: '', branch_id: '', description: '', date: '', amount: '' });
    setSelectedDateModal(null);
    setEditExpenseId(null);
    setIsEditMode(false);
  };

  const handleEdit = (expense) => {
    const modalElement = document.getElementById('add-units');
    const modal = new bootstrap.Modal(modalElement);
    modal.show();
    setFormData({
      expense_category_id: expense.category_id,
      date: expense.date,
      amount: expense.amount,
      description: expense.description || '',
      branch_id: expense.branch_id
    });
    setSelectedDateModal(expense.date ? new Date(expense.date) : null);
    setEditExpenseId(expense.id);
    setIsEditMode(true);
  };

  const renderCollapseTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Collapse
    </Tooltip>
  );

  const expenseCategoryOptions = expenseCategories.map(category => ({
    value: category.id,
    label: category.name
  }));

  const branchOptions = branches.map(branch => ({
    value: branch.id,
    label: branch.name
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        const selectedCategory = expenseCategories.find(
          category => category.id === parseInt(formData.expense_category_id)
        );
        const formattedData = {
          name: selectedCategory ? selectedCategory.name : '',
          expense_category_id: formData.expense_category_id,
          branch_id: formData.branch_id,
          description: formData.description,
          date: selectedDateModal ? selectedDateModal.toISOString().split('T')[0] : '',
          amount: parseInt(formData.amount),
        };
        const response = await apiClient.post('/store-update-expense', {
          ...formattedData,
          ...(isEditMode && { id: editExpenseId })
        });
        const Apidata = await response;
        if (Apidata.status) {
          setFormData({ name: '', description: '', date: '', amount: '', expense_category_id: '', branch_id: '' });
          setErrors({ expense_category_id: '', date: '', amount: '' });
          setSelectedDateModal(null);
          setEditExpenseId(null);
          setIsEditMode(false);
          showToast(Apidata.message, 'success', 'Expense Operation!');
          hasFetched.current = false;
          await fetchExpenses();
          const modalElement = document.getElementById('add-units');
          const modal = bootstrap.Modal.getInstance(modalElement);
          modal.hide();
        }
      } catch (error) {
        showToast(error.message || 'An unexpected error occurred: Please contact the support team.', 'error', 'Expense Operation!');
      }
    }
  };

  const handleDateChangeModal = (date) => {
    setSelectedDateModal(date);
    const dateValue = date ? date.toISOString() : '';
    setFormData({ ...formData, date: dateValue });
    setErrors((prev) => ({ ...prev, date: validateField('date', dateValue) }));
  };

  useEffect(() => {
    fetchExpenses();
  }, []);

  const fetchExpenses = async () => {
    if (hasFetched.current) return;
    setLoading(true);
    try {
      const response = await apiClient.post('/get-all-expenses', {
        pageIndex: 0,
        pageSize: 50,
        previousPageIndex: 0,
      });
      const data = await response;
      if (data.status) {
        setExpenses(data.data);
        setFilteredExpenses(data.data);
      } else {
        setExpenses([]);
        setFilteredExpenses([]);
      }
    } catch (error) {
      showToast(error.message || 'Failed to fetch expenses', 'error', 'Expenses');
    } finally {
      setLoading(false);
      hasFetched.current = true;
    }
  };

  const handleSearchExpense = (e) => {
    const term = e.target.value.trim().toLowerCase();
    if (!term) {
      setFilteredExpenses([]);
      setIsSearching(false);
      return;
    }
    const filtered = expenses.filter((expense) =>
      expense.name?.toLowerCase().includes(term) ||
      expense.amount?.toString().includes(term)
    );
    setFilteredExpenses(filtered);
    setIsSearching(true);
  };

  const showConfirmationAlert = (record) => {
    MySwal.fire({
      title: 'Are you sure?',
      text: 'You won\'t be able to revert this!',
      showCancelButton: true,
      confirmButtonColor: '#00ff00',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonColor: '#ff0000',
      cancelButtonText: 'Cancel',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          const response = await apiClient.post('/delete-expense', {
            id: record.id
          });
          const data = await response;
          if (data.status) {
            showToast('Expense deleted successfully', 'success', 'Expense Operation!');
            hasFetched.current = false;
            await fetchExpenses();
          } else {
            showToast(data.message || 'Failed to delete expense', 'error', 'Expense Operation!');
          }
        } catch (error) {
          showToast(error.message || 'Failed to delete expense', 'error', 'Expense Operation!');
        } finally {
          setLoading(false);
        }
      } else {
        MySwal.close();
      }
    });
  };

  const columns = [
    {
      title: "Category Name",
      dataIndex: "name",
      sorter: (a, b) => a.name.length - b.name.length,
    },
    {
      title: "Date",
      dataIndex: "date",
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
      render: (text) => {
        const date = new Date(text);
        return isNaN(date) ? 'N/A' : date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }).split('/').join('-');
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      sorter: (a, b) => a.amount - b.amount,
      render: (text) => `$${text}`,
    },
    {
      title: "Description",
      dataIndex: "description",
      sorter: (a, b) => (a.description || '').length - (b.description || '').length,
      render: (text) => text || '-',
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <td className="action-table-data">
          <div className="edit-delete-action">
            <Link className="me-2 p-2" to="#" onClick={() => handleEdit(record)}>
              <i data-feather="edit" className="feather-edit"></i>
            </Link>
            <Link className="confirm-text p-2" to="#" onClick={() => showConfirmationAlert(record)}>
              <i data-feather="trash-2" className="feather-trash-2"></i>
            </Link>
          </div>
        </td>
      ),
    },
  ];

  const displayExpenses = isSearching ? filteredExpenses : expenses;
  const MySwal = withReactContent(Swal);

  return (
    <div>
      <Loader loading={loading} />
      <div className="page-wrapper" style={{ position: 'relative', minHeight: '100vh' }}>
        <div className="content" style={{ opacity: loading ? 0.5 : 1 }}>
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title d-flex align-items-center">
                <div>
                  <h4>Expenses</h4>
                  <h6>Manage your Expenses</h6>
                </div>
              </div>
            </div>
            <ul className="table-top-head">
              <li>
                <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                  <Link
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    id="collapse-header"
                    className={data ? "active" : ""}
                    onClick={() => dispatch(setToogleHeader(!data))}
                  >
                    <ChevronUp />
                  </Link>
                </OverlayTrigger>
              </li>
            </ul>
            <div className="page-btn">
              <Link to="#" className="btn btn-added" onClick={handleAdd}>
                <PlusCircle className="me-2" />
                Add Expenses
              </Link>
            </div>
          </div>
          <div className="card table-list-card">
            <div className="card-body">
              <div className="table-top">
                <div className="search-set">
                  <div className="search-input">
                    <input
                      type="text"
                      placeholder="Search"
                      className="form-control form-control-sm formsearch"
                      onChange={handleSearchExpense}
                    />
                    <Link to className="btn btn-searchset">
                      <i data-feather="search" className="feather-search" />
                    </Link>
                  </div>
                </div>
              </div>
              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={displayExpenses}
                  rowKey={(record) => record.id}
                  rowSelection={null}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal fade" id="add-units">
        <div className="modal-dialog modal-dialog-centered custom-modal-two">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header border-0 custom-modal-header d-flex justify-content-between align-items-center">
                  <div className="page-title">
                    <h4>{isEditMode ? 'Update Expense' : 'Add Expense'}</h4>
                  </div>
                  <button type="button" className="close" data-bs-dismiss="modal" aria-label="Close">
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
                <div className="modal-body custom-modal-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">Expense Category</label>
                          <Select
                            key={`category-${formData.expense_category_id}`}
                            value={expenseCategoryOptions.find(option => option.value === formData.expense_category_id)}
                            onChange={handleCategoryChange}
                            options={expenseCategoryOptions}
                            placeholder="Select Category"
                            isClearable={true}
                          />
                          {errors.expense_category_id && (
                            <div className="text-danger">{errors.expense_category_id}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">Branches</label>
                          <Select
                            key={`branch-${formData.branch_id}`}
                            value={branchOptions.find(option => option.value === formData.branch_id)}
                            onChange={handleBranchChange}
                            options={branchOptions}
                            placeholder="Select Branch"
                            isClearable={true}
                          />
                          {errors.branch_id && (
                            <div className="text-danger">{errors.branch_id}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">Date</label>
                          <div className="input-group date-group">
                            <span className="input-group-text">
                              <i className="bi bi-calendar-date"></i>
                            </span>
                            <DatePicker
                              selected={selectedDateModal}
                              onChange={handleDateChangeModal}
                              dateFormat="dd/MM/yyyy"
                              placeholderText="Choose Date"
                              className="form-control datetimepicker"
                            />
                          </div>
                          {errors.date && (
                            <div className="text-danger">{errors.date}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Amount</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="$"
                            name="amount"
                            value={formData.amount}
                            onChange={handleInputChange}
                          />
                          {errors.amount && (
                            <div className="text-danger">{errors.amount}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div className="edit-add card">
                          <div className="edit-add">
                            <label className="form-label">Description</label>
                          </div>
                          <div className="card-body-list input-blocks mb-0">
                            <textarea
                              className="form-control"
                              name="description"
                              value={formData.description}
                              onChange={handleInputChange}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="modal-footer-btn">
                      <button type="button" className="btn btn-cancel me-2" data-bs-dismiss="modal">
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-submit">
                        {isEditMode ? 'Update' : 'Submit'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpensesList;
